import React, { useCallback, useState } from 'react';
import { DialogContent, DialogActions, Chip } from '@mui/material';
import { ErrorsMessagesComponent } from '@warehouse/shared/ui';
import Button from '../../../components/library/Button';
import Dialog from '../../../components/library/Dialog';
import DialogTitle, { CloseButton } from '../../../components/library/DialogTitle';
import UploadDropzone from '../../../components/library/UploadDropzone';
import { useImportMEC } from '../../../utils/hooks/titles/useImportMEC';
import Loader from '../../../components/library/Loader';

interface ImportMECDialogProps {
	open: boolean;
	onClose: () => void;
}

export function ImportMECDialog({ open, onClose }: ImportMECDialogProps) {
	const { importMEC, errors, loading, clearErrors } = useImportMEC();
	const [selectedFile, setSelectedFile] = useState<File | null>(null);

	const handleFileSelect = useCallback((files: File[]) => {
		const file = files[0];
		if (file) {
			setSelectedFile(file);
		}
	}, []);

	const handleClose = useCallback(() => {
		setSelectedFile(null);
		clearErrors();
		onClose();
	}, [onClose, clearErrors]);

	const readFileContent = useCallback(
		(file: File): Promise<string> =>
			new Promise((resolve, reject) => {
				const reader = new FileReader();
				reader.onload = () => {
					if (typeof reader.result === 'string') resolve(reader.result);
					reject(new Error('Unable to parse file as text'));
				};
				reader.onerror = reject;
				reader.readAsText(file);
			}),
		[],
	);

	const handleImport = useCallback(async () => {
		if (!selectedFile) return;

		const fileContent = await readFileContent(selectedFile);
		await importMEC(fileContent);
	}, [selectedFile, readFileContent, importMEC]);

	return (
		<Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
			<DialogTitle>
				Import MEC
				<CloseButton onClose={handleClose} />
			</DialogTitle>

			<DialogContent sx={{ padding: 2, display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
				{selectedFile && (
					<Chip
						label={selectedFile.name}
						onDelete={() => {
							setSelectedFile(null);
							clearErrors();
						}}
						sx={{
							width: '80%',
							margin: '12px auto',
							borderRadius: 2,
							justifyContent: 'space-between',
							bgcolor: 'grey.100',
							boxShadow: 1,
							fontWeight: 500,
							'& .MuiChip-deleteIcon': {
								color: 'grey.600',
								'&:hover': {
									color: 'grey.800',
								},
							},
							'&:hover': {
								bgcolor: 'grey.200',
							},
						}}
					/>
				)}
				{!selectedFile && <UploadDropzone onDrop={handleFileSelect} />}

				{errors.length > 0 && <ErrorsMessagesComponent errorsList={errors} />}
			</DialogContent>

			<DialogActions sx={{ padding: 2 }}>
				<Button nxstyle="secondary-black" onClick={handleClose} disabled={loading}>
					Cancel
				</Button>
				<Button
					nxstyle="primary-blue"
					onClick={handleImport}
					disabled={!selectedFile || loading}
					endIcon={loading ? <Loader size={20} color="#fff" /> : null}
				>
					{loading ? 'Importing...' : 'Import'}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
